import axios from 'axios';

import _keyBy from 'lodash/keyBy';

import {
  computed,
  ref,
} from 'vue';

import { useI18n } from 'vue-i18n';

import { useToast } from 'primevue/usetoast';

import {
  CAMPAIGN_BID_UNITS,
  CAMPAIGN_PAYMENT_TYPE_CPM,
  TARGETING_TYPE_DISABLED,
  TARGETING_TYPE_WHITE_LIST,
} from '@/libs/consts';

import type {
  CampaignSubidBid,
  CampaignSubidSettings,
  GenericObject,
} from '@/types';

/**
 * Блокировка субайдов и их индивидуальные ставки в статистике по субайдам.
 *
 * Работает, только когда в фильтрах выбрана ровно одна кампания: и блокировка, и ставка
 * принадлежат кампании, а не субайду, и без неё непонятно, чьи именно показывать.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export default (statisticsFilters: GenericObject) => {
  const i18n = useI18n();

  const toast = useToast();

  const campaignId = computed((): number | null => (statisticsFilters.campaign_id?.length === 1 ? statisticsFilters.campaign_id[0] : null));

  /**
   * Настройки заменяются целиком, а не мержатся: при bulk-биддинге ключа subid_bids в ответе
   * нет, и слияние оставило бы ставки предыдущей кампании висеть напротив чужих субайдов.
   * Пустой ref — «кампания не выбрана либо неизвестна», подсветка строк тогда не работает.
   */
  const settings = ref<CampaignSubidSettings | null>(null);

  const fetchSettings = async (): Promise<void> => {
    if (!campaignId.value) {
      settings.value = null;

      return;
    }

    try {
      const response = await axios.get(`/api/v3/campaigns/${campaignId.value}`);

      settings.value = response.data;
    } catch (error) {
      // Запрос сопутствующий: его падение не должно ронять загрузку самой статистики
      settings.value = null;
    }
  };

  /**
   * Пока настройки не прочитаны, состояние субайда неизвестно — контролы не показываем.
   * Иначе у заблокированного субайда висел бы крестик «заблокировать», а клик слал бы
   * повторный блок вместо разблокировки. Так же выглядит и 403 на чужой кампании:
   * лимитед-менеджер видит её строки в статистике (скоуп там по ИЛИ), но `operate` не пускает.
   * У удалённой кампании блокировать нечего: подсветка и ставки остаются, контролов нет.
   */
  const settingsLoaded = computed((): boolean => settings.value !== null && !settings.value.deleted_at);

  const subidBidsMap = computed((): Record<string, CampaignSubidBid> => _keyBy(settings.value?.subid_bids ?? [], 'subid_uuid'));

  const subidIsBlocked = (subidUuid: string): boolean => {
    if (!settings.value || settings.value.targeting_by_subids === TARGETING_TYPE_DISABLED) {
      return false;
    }

    // В белом списке блокировкой считается отсутствие в списке, в чёрном — наличие
    return (settings.value.targeting_by_subids === TARGETING_TYPE_WHITE_LIST) !== (settings.value.subid_uuids ?? []).includes(subidUuid);
  };

  /**
   * Индивидуальная ставка кампании по субайду в тех же единицах, в которых рядом стоит
   * колонка CPC: API отдаёт цену за тысячу единиц, а CPC-кампания платит за один клик.
   * У CPM-кампании цена за тысячу и есть CPM, её делить не на что.
   */
  const subidBid = (subidUuid: string): number | null => {
    const customBid = subidBidsMap.value[subidUuid];

    if (!customBid) {
      return null;
    }

    return settings.value?.pricing_model === CAMPAIGN_PAYMENT_TYPE_CPM ? customBid.bid : customBid.bid / CAMPAIGN_BID_UNITS;
  };

  const subidBidLabel = computed((): string => (settings.value?.pricing_model === CAMPAIGN_PAYMENT_TYPE_CPM ? 'CPM' : 'CPC'));

  const subidLoading = ref<Record<string, boolean>>({});

  const toggleSubidBlocked = async (subidUuid: string): Promise<void> => {
    if (!campaignId.value) {
      return;
    }

    const blocked = subidIsBlocked(subidUuid);

    subidLoading.value[subidUuid] = true;

    try {
      const url = `/api/v3/campaigns/${campaignId.value}/blocked-subids`;

      const response = blocked
        ? await axios.delete(url, { data: { subid_uuids: [subidUuid] } })
        : await axios.post(url, { subid_uuids: [subidUuid] });

      // Ответ — та же кампания, что и в fetchSettings: состояние перезаписывается ответом
      // сервера, а не досочиняется на клиенте. Иначе локальный targeting_by_subids расходится
      // с реальным — блокировка первого субайда переводит кампанию в чёрный список на бэке.
      settings.value = response.data;

      toast.add({
        severity: 'success',
        summary: i18n.t('success'),
        detail: i18n.t(blocked ? 'success_subid_unblock' : 'success_subid_block'),
        life: 2000,
      });
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: i18n.t('error'),
        detail: i18n.t('unknown_error'),
        life: 2000,
      });
    }

    subidLoading.value[subidUuid] = false;
  };

  const subidRowClass = (subidUuid: string): string => (subidIsBlocked(subidUuid) ? 'statistics-table-row-subid-declined' : '');

  return {
    campaignId,
    settingsLoaded,
    fetchSettings,
    subidIsBlocked,
    subidBid,
    subidBidLabel,
    subidLoading,
    toggleSubidBlocked,
    subidRowClass,
  };
};
